/**
 * Central route table.
 *
 * Every page is loaded via lazyWithPreload so each route ships as its own chunk
 * (code splitting) and can be warmed ahead of navigation (route preloading).
 *
 * The app's sections are NOT hand-written here — they are generated from
 * config/navigation.js, the same list the app bar renders. One source means a
 * nav item can never point at a missing route, and a route can never go
 * unreachable.
 *
 * Variant handling, both without forking the table:
 *   - locally, the product appears in the URL as a `basename` ('/cca' |
 *     '/eia'), applied by the router rather than written into the paths; on a
 *     deployed host the URL is plain ('/manage-portfolio'), since the hostname
 *     already names the product (router/basename.js);
 *   - sections are filtered by mode, so a product never registers a route it
 *     doesn't own — typing a CCA-only URL under EIA falls through to NotFound.
 */
import { createBrowserRouter } from 'react-router-dom';

import { PageContainer } from '@components/common/PageContainer';
import { getLoginFlags } from '@config/loginMode';
import { getDefaultPath, getSections } from '@config/navigation';
import { AppLayout } from '@layouts/AppLayout';
import { RequireAuth } from '@router/guards/RequireAuth';
import { RequirePermission } from '@router/guards/RequirePermission';
import { RequireSignedOut } from '@router/guards/RequireSignedOut';
import { resolveAppMode } from '@variant/appMode';

import { ensureVariantPrefix, getBasename } from './basename';
import { HomePage, LocalLoginPage, NotFoundPage } from './lazyPages';

const mode = resolveAppMode();

/*
 * The DEFAULT sign-in screen has no address of its own. RequireAuth shows it at
 * the landing URL ('/'; '/cca/' or '/eia/' locally), moving a signed-out visitor
 * there from any other path, so that is where they meet it — and where a
 * signed-in visitor never sees it. (The local form ALSO has /login, on every
 * host — see the route below.)
 *
 * Which screen: decided once, like `mode` and `basename`, because host and build
 * env cannot change while the page is open (config/loginMode.js).
 *
 * The two differ in FRAME, following legacy: Home is a page of the site, shown
 * inside the shell (header + footer, as Home.vue renders inside App.vue), while
 * the local form is a standalone sign-in on its own backdrop. AppLayout is
 * reused for the Home frame rather than a second "public layout", so the header
 * and footer cannot drift between signed-in and signed-out.
 */
const signInScreen = getLoginFlags().advisoryLogin ? (
  <AppLayout>
    <HomePage />
  </AppLayout>
) : (
  <LocalLoginPage />
);

// Resolve the prefix and get the address bar to agree with it BEFORE the router
// reads the URL: locally, an unprefixed entry point ('/', a bookmark) would
// otherwise fall outside the basename and match nothing; on a deployed host, an
// old prefixed bookmark would otherwise 404.
const basename = getBasename(mode);
ensureVariantPrefix(basename);

/**
 * One section -> one child route. Child paths are relative to the layout's '/',
 * hence the leading slash is stripped. A section with `permission` gets wrapped
 * in the RBAC guard here, so the gating lives with the route, not in the page.
 */
function toRoute(section) {
  const Page = section.page;

  // <main> is full-bleed, so pages get the centred padded column here unless
  // they opt out — a page with a side rail needs the rail flush to the edge.
  const page = section.fullBleed ? (
    <Page />
  ) : (
    <PageContainer>
      <Page />
    </PageContainer>
  );

  const element = section.permission ? (
    <RequirePermission permission={section.permission}>{page}</RequirePermission>
  ) : (
    page
  );

  return { path: section.path.replace(/^\//, ''), element };
}

export const router = createBrowserRouter(
  [
    // The local sign-in form's own address, on EVERY host (user order
    // 2026-10-23; legacy has /login everywhere too). Signed in, it moves on to
    // the default section, as legacy's guard does (router/index.js:137).
    {
      path: '/login',
      element: (
        <RequireSignedOut to={getDefaultPath(mode)}>
          <LocalLoginPage />
        </RequireSignedOut>
      ),
    },
    {
      path: '/',
      element: (
        // afterSignIn: a FRESH sign-in on the local form goes to the default
        // section, as legacy's Login.vue does (:188). Opening '/' while already
        // signed in does not — that shows Home, below.
        <RequireAuth fallback={signInScreen} afterSignIn={getDefaultPath(mode)}>
          <AppLayout />
        </RequireAuth>
      ),
      children: [
        // '/' is the Home page for a signed-in user too, exactly as legacy: its
        // router does not redirect '/', and Home.vue only forwards a signed-in
        // user when the path is '/home' (isHomePage, Home.vue:139). The app bar
        // above it lists the sections this account can open, none highlighted.
        { index: true, element: <HomePage /> },
        ...getSections(mode).map(toRoute),
        // "Go to Home" is legacy's /dashboard/manageaccounts: the default section.
        { path: '*', element: <NotFoundPage homePath={getDefaultPath(mode)} /> },
      ],
    },
  ],
  // '' (deployed, no prefix) is spelled '/' for the router, its own "no basename".
  { basename: basename || '/' },
);
